<template>
  <div>
    <v2-modal
      :show="show"
      size="xl"
      @close="onCloseClick"
    >
      <!-- Header: title + step indicator, matching the Ship All wizard -->
      <template #header>
        <div class="v2-ship-wizard__header">
          <div class="v2-ship-wizard__title-row">
            <h2 class="v2-ship-wizard__title">
              Remove Products
            </h2>
          </div>
          <div class="v2-ship-wizard__steps">
            <div
              v-for="(label, idx) in stepLabels"
              :key="label"
              class="v2-ship-wizard__step"
              :class="{
                'v2-ship-wizard__step--active': currentStep === idx + 1,
                'v2-ship-wizard__step--completed': currentStep > idx + 1,
                'v2-ship-wizard__step--pending': currentStep < idx + 1,
              }"
            >
              {{ label }}
            </div>
          </div>
        </div>
      </template>

      <v2-ship-all-collection-step
        v-if="currentStep === 1"
        :selected-collection-id="selectedCollectionId"
        subtitle="Choose the collection whose shipments you want to remove products from."
        @select="onSelectCollection"
      />

      <!-- Reuses the Ship All product step, pointed at the products currently
           sitting on shipments instead of the ready-to-ship ones. -->
      <v2-ship-all-product-step
        v-if="currentStep === 2"
        :collection-id="selectedCollectionId"
        :collection-name="selectedCollectionName"
        :selected-product-ids="selectedProductIds"
        :products-loader="loadReturnableProducts"
        title="Select Products to Remove"
        subtitle-prefix="Products currently on shipments in"
        subtitle-suffix="that can be removed."
        @update="onUpdateProducts"
      />

      <v2-unassign-shipment-step
        v-if="currentStep === 3"
        :selected-products="selectedProducts"
        :selected-shipment-ids="selectedShipmentIds"
        @update="onUpdateShipments"
      />

      <!-- Emptied-shipment confirmation: deleting is not undoable, so it is
           always confirmed and never implicit. -->
      <v2-modal
        :show="showEmptyWarning"
        title="Some shipments will be deleted"
        @close="showEmptyWarning = false"
      >
        <p>
          This removes every product from
          {{ shipmentsToEmpty.length }} shipment(s), so
          {{ shipmentsToEmpty.length === 1 ? 'it' : 'they' }} will be deleted:
        </p>
        <ul class="v2-unassign-wizard__warn-list">
          <li
            v-for="shipment in shipmentsToEmpty"
            :key="shipment.shipmentId"
          >
            {{ shipment.shipmentSeqId }}
          </li>
        </ul>
        <p>The products themselves go back to their orders.</p>
        <template #footer>
          <v2-button
            variant="outline"
            @click="showEmptyWarning = false"
          >
            Cancel
          </v2-button>
          <v2-button
            variant="destructive"
            :disabled="isSubmitting"
            @click="submit"
          >
            {{ isSubmitting ? 'Removing...' : 'Remove and delete' }}
          </v2-button>
        </template>
      </v2-modal>

      <template #footer>
        <v2-button
          variant="outline"
          :disabled="currentStep === 1"
          @click="prevStep"
        >
          Back
        </v2-button>
        <v2-button
          v-if="currentStep < 3"
          variant="primary"
          :disabled="!canProceed"
          @click="nextStep"
        >
          Next
        </v2-button>
        <v2-button
          v-else
          variant="primary"
          :disabled="!selectedShipmentIds.length || isSubmitting"
          @click="onConfirm"
        >
          <v2-spinner
            v-if="isSubmitting"
            size="sm"
          />
          Remove Products ({{ selectedShipmentIds.length }})
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import store from '@/store'
import {
  GET_RETURNABLE_PRODUCTS,
  BULK_RETURN_PRODUCTS_TO_ORDER,
} from '@/store/modules/shipment.module'
import { apiToastSuccess, apiToastWarning, apiToastErrorV2 } from '@/@core/utils/toast'
import V2ShipAllCollectionStep from '@/views/v2/orders/ship-all/steps/V2ShipAllCollectionStep.vue'
import V2ShipAllProductStep from '@/views/v2/orders/ship-all/steps/V2ShipAllProductStep.vue'
import V2UnassignShipmentStep from './steps/V2UnassignShipmentStep.vue'

export default {
  name: 'V2UnassignProductsWizard',
  components: {
    V2Modal,
    V2Button,
    V2Spinner,
    V2ShipAllCollectionStep,
    V2ShipAllProductStep,
    V2UnassignShipmentStep,
  },
  props: {
    show: { type: Boolean, default: false },
  },
  emits: ['close', 'removed'],
  data() {
    return {
      stepLabels: ['Collection', 'Products', 'Shipments'],
      currentStep: 1,
      selectedCollectionId: '',
      selectedCollectionName: '',
      selectedProductIds: [],
      selectedShipmentIds: [],
      // Rows from the returnable-products endpoint, kept so step 3 knows which
      // shipments hold each product and how many products each one has.
      returnableProducts: [],
      isSubmitting: false,
      showEmptyWarning: false,
    }
  },
  computed: {
    selectedProducts() {
      return this.returnableProducts.filter(product => this.selectedProductIds.includes(product.productId))
    },
    shipmentsToEmpty() {
      const byId = {}
      this.selectedProducts.forEach(product => {
        product.shipments.forEach(shipment => {
          if (this.selectedShipmentIds.includes(shipment.shipmentId)) {
            byId[shipment.shipmentId] = shipment
          }
        })
      })
      return Object.values(byId).filter(shipment => {
        const selectedOnThisShipment = this.selectedProducts.filter(product => product.shipments.some(
          ref => ref.shipmentId === shipment.shipmentId,
        )).length
        return selectedOnThisShipment >= shipment.productCount
      })
    },
    canProceed() {
      if (this.currentStep === 1) return !!this.selectedCollectionId
      if (this.currentStep === 2) return this.selectedProductIds.length > 0
      return this.selectedShipmentIds.length > 0
    },
  },
  watch: {
    show(isShown) {
      if (isShown) this.reset()
    },
  },
  methods: {
    reset() {
      this.currentStep = 1
      this.selectedCollectionId = ''
      this.selectedCollectionName = ''
      this.selectedProductIds = []
      this.selectedShipmentIds = []
      this.returnableProducts = []
      this.isSubmitting = false
      this.showEmptyWarning = false
    },
    // Passed into the shared product step so it lists the products currently on
    // shipments rather than the ready-to-ship ones. Scoped by collectionId,
    // which the backend resolves through the collection's orders.
    async loadReturnableProducts(collectionId) {
      const res = await store.dispatch(GET_RETURNABLE_PRODUCTS, {
        collectionId,
      })
      this.returnableProducts = res?.data?.data?.products || []
      return this.returnableProducts
    },
    onSelectCollection({ id, name }) {
      if (id !== this.selectedCollectionId) {
        // Changing collection invalidates everything picked after it.
        this.selectedProductIds = []
        this.selectedShipmentIds = []
        this.returnableProducts = []
      }
      this.selectedCollectionId = id
      this.selectedCollectionName = name
    },
    onUpdateProducts(productIds) {
      this.selectedProductIds = productIds
    },
    onUpdateShipments(shipmentIds) {
      this.selectedShipmentIds = shipmentIds
    },
    nextStep() {
      if (this.canProceed) this.currentStep += 1
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep -= 1
    },
    onCloseClick() {
      if (!this.isSubmitting) this.$emit('close')
    },
    onConfirm() {
      if (this.shipmentsToEmpty.length) {
        this.showEmptyWarning = true
        return
      }
      this.submit()
    },
    // A bulk run reports per shipment. Showing only the summary hides why a
    // shipment was skipped - the reason sits on the result row, so surface it
    // rather than leaving "1 of 2 updated" unexplained.
    reportBulkResult(res) {
      const data = (res && res.data) || {}
      const results = (data.data && data.data.results) || []
      const failures = results.filter(result => !result.succeeded)
      if (!failures.length) {
        apiToastSuccess(data.message || 'Products removed from their shipments')
        return
      }
      apiToastWarning(
        data.message || 'Some shipments were not updated',
        undefined,
        failures
          .map(failure => `${failure.shipmentId}: ${failure.message}`)
          .join('\n'),
      )
    },
    async submit() {
      this.isSubmitting = true
      try {
        const res = await store.dispatch(BULK_RETURN_PRODUCTS_TO_ORDER, {
          shipmentIds: this.selectedShipmentIds,
          productIds: this.selectedProductIds,
        })
        this.reportBulkResult(res)
        this.showEmptyWarning = false
        this.$emit('removed')
        this.$emit('close')
      } catch (error) {
        apiToastErrorV2(error)
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Mirrors the Ship All wizard's header/step-indicator, whose styles are scoped
// to that component and so cannot be imported.
.v2-ship-wizard {
  &__header {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-3);
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-family: var(--v2-font-display);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0;
  }

  &__steps {
    display: flex;
    align-items: stretch;
    gap: var(--v2-space-2);
    width: 100%;
  }

  &__step {
    flex: 1;
    text-align: center;
    padding: var(--v2-space-2) var(--v2-space-3);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--v2-muted-foreground);
    border-bottom: 2px solid var(--v2-glass-stroke);
    transition: color 0.15s ease, border-color 0.15s ease;

    &--active {
      color: var(--v2-primary);
      border-bottom-color: var(--v2-primary);
    }

    &--completed {
      color: oklch(0.62 0.18 145);
      border-bottom-color: oklch(0.62 0.18 145);
    }

    &--pending {
      color: var(--v2-muted-foreground);
    }
  }
}

.v2-unassign-wizard__warn-list {
  margin: 0.5rem 0;
  padding-left: 1.25rem;
}
</style>
